import { useParams, useLocation, Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchWithAuth } from "@/lib/api/client";
import type { Licitacion } from "@/types/licitacion";
import { LicitacionDetailHeader } from "@/components/licitaciones/licitacion-detail-header";
import { LicitacionTabBar } from "@/components/licitaciones/licitacion-tab-bar";
import { Skeleton } from "@/components/ui/skeleton";

function LoadingSkeleton() {
  return (
    <div className="bg-white p-6 border-b border-gray-200 space-y-4">
      <Skeleton className="h-8 w-2/3" />
      <Skeleton className="h-4 w-1/3" />
      <div className="flex gap-4">
        <Skeleton className="h-6 w-24" />
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-6 w-28" />
      </div>
    </div>
  );
}

export default function LicitacionDetailLayout() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  // El EDITOR de una propuesta (/propuestas/:proposalId) es una pantalla de
  // trabajo a pantalla completa: no mostramos la cabecera grande de la
  // licitación ni los tabs ni el padding del layout, para que el editor use
  // toda la altura disponible. El resto de pestañas sí los muestran.
  const isProposalEditor = /\/propuestas\/[^/]+\/?$/.test(location.pathname);

  const { data: licitacion, isLoading, isError, error } = useQuery<Licitacion>({
    queryKey: ["licitacion", id],
    queryFn: () => fetchWithAuth(`/api/v1/licitaciones/${id}`),
    enabled: !!id,
  });

  if (isProposalEditor) {
    // El propio editor gestiona su cabecera, scroll y estados de carga.
    return (
      <div className="h-full">
        <Outlet />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div>
        <LoadingSkeleton />
        <div className="p-6"><Skeleton className="h-64 w-full" /></div>
      </div>
    );
  }

  if (isError || !licitacion) {
    return (
      <div className="bg-white p-6 border-b border-gray-200">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-red-600 font-medium">Error al cargar la licitación</p>
          <p className="text-sm text-gray-500 mt-1">
            {error instanceof Error ? error.message : "No se pudo cargar la licitación"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <LicitacionDetailHeader licitacion={licitacion} />
      <LicitacionTabBar licitacionId={id!} documentCount={licitacion.document_count} />
      <div className="p-6">
        <Outlet />
      </div>
    </div>
  );
}
